//#region assets/collection-quick-filters.ts
/** True when the URL carries any storefront filter query param. */
function hasStorefrontFilterParams(search) {
	const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
	for (const key of params.keys()) if (key.startsWith("filter.")) return true;
	return false;
}
/** True when a chip's filter param/value is present in the URL. */
function isQuickFilterParamActive(search, param, value) {
	const trimmedParam = param.trim();
	const trimmedValue = value.trim();
	if (!trimmedParam || !trimmedValue) return false;
	const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
	const needle = trimmedValue.toLowerCase();
	return params.getAll(trimmedParam).some((entry) => entry.toLowerCase() === needle);
}
/**
* Toggle a facet on the current PLP URL while preserving other active filters.
* Chips never switch collection: when the facet yields nothing the PLP shows its
* own empty state instead of redirecting.
*/
function buildQuickFilterToggleUrl(chip, context) {
	const param = chip.filter_param?.trim() ?? "";
	const value = chip.filter_value?.trim() ?? "";
	if (!param || !value) return context.pathname;
	const pathname = context.pathname;
	const params = new URLSearchParams(context.search);
	const needle = value.toLowerCase();
	const current = params.getAll(param);
	const hasValue = current.some((entry) => entry.toLowerCase() === needle);
	if (context.isActive) {
		const remaining = current.filter((entry) => entry.toLowerCase() !== needle);
		params.delete(param);
		for (const entry of remaining) params.append(param, entry);
	} else if (!hasValue) params.append(param, value);
	params.delete("page");
	const query = params.toString();
	return query ? `${pathname}?${query}` : pathname;
}
//#endregion
//#region assets/vp-plp-quick-categories.ts
/**
* Mobile quick-category scroller: drag-to-scroll + fill progress bar.
* Facet chips use the PLP facets form so filters combine and scroll is preserved.
* Active chip state is rendered from Liquid (`active_values`); JS only syncs on
* click (optimistic) and popstate.
*/
var DRAG_THRESHOLD = 8;
var ACTIVE_CLASS = "product-lister__quick-category-link--active";
/** @typedef {{ updateFiltersByURL?: (url: string) => void }} FacetsFormElement */
var VpQuickCategories = class extends HTMLElement {
	/** @type {HTMLElement | null} */
	#scroller = null;
	/** @type {HTMLElement | null} */
	#bar = null;
	/** @type {number | null} */
	#rafId = null;
	#dragging = false;
	#startX = 0;
	#startScroll = 0;
	#moved = false;
	connectedCallback() {
		this.#scroller = this.querySelector("[data-vp-quick-categories-scroller]");
		this.#bar = this.querySelector("[data-vp-quick-categories-progress]");
		if (!this.#scroller) return;
		this.#scroller.addEventListener("scroll", this.#onScroll, { passive: true });
		this.#scroller.addEventListener("pointerdown", this.#onPointerDown);
		this.#scroller.addEventListener("click", this.#onFacetToggleClick, true);
		window.addEventListener("resize", this.#onScroll, { passive: true });
		window.addEventListener("popstate", this.#onPopState);
		this.#updateProgress();
	}
	disconnectedCallback() {
		this.#scroller?.removeEventListener("scroll", this.#onScroll);
		this.#scroller?.removeEventListener("pointerdown", this.#onPointerDown);
		this.#scroller?.removeEventListener("click", this.#onFacetToggleClick, true);
		window.removeEventListener("resize", this.#onScroll);
		window.removeEventListener("popstate", this.#onPopState);
		if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
		this.#unbindDrag();
	}
	#onPopState = () => {
		this.#syncActiveChips(window.location.pathname + window.location.search);
	};
	/**
	* Mirror URL filter state onto chip active classes / data attrs.
	* @param {string} url
	*/
	#syncActiveChips(url) {
		let search = "";
		try {
			search = new URL(url, window.location.origin).search;
		} catch {
			return;
		}
		const hasFilters = hasStorefrontFilterParams(search);
		for (const link of this.querySelectorAll("a[data-vp-facet-toggle]")) {
			if (!(link instanceof HTMLAnchorElement)) continue;
			const param = link.dataset.vpFacetParam ?? "";
			const value = link.dataset.vpFacetValue ?? "";
			const active = param && value ? isQuickFilterParamActive(search, param, value) : hasFilters === false;
			link.classList.toggle(ACTIVE_CLASS, active);
			link.dataset.vpFacetActive = active ? "true" : "false";
			if (active) link.setAttribute("aria-current", "page");
			else link.removeAttribute("aria-current");
		}
	}
	/** @param {MouseEvent} event */
	#onFacetToggleClick = (event) => {
		if (!(event.target instanceof Element)) return;
		const link = event.target.closest("a[data-vp-facet-toggle]");
		if (!(link instanceof HTMLAnchorElement)) return;
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
		event.preventDefault();
		const param = link.dataset.vpFacetParam ?? "";
		const value = link.dataset.vpFacetValue ?? "";
		const isActive = link.dataset.vpFacetActive === "true";
		let url = link.href;
		if (param && value) url = buildQuickFilterToggleUrl({
			filter_param: param,
			filter_value: value
		}, {
			pathname: window.location.pathname,
			search: window.location.search,
			isActive
		});
		else if (isActive) return;
		else url = window.location.pathname;
		try {
			const next = new URL(url, window.location.origin);
			url = `${next.pathname}${next.search}`;
		} catch {
			window.location.assign(url);
			return;
		}
		this.#syncActiveChips(url);
		const facetsForm = document.querySelector("facets-form-component");
		if (facetsForm && typeof facetsForm.updateFiltersByURL === "function") {
			facetsForm.updateFiltersByURL(url);
			return;
		}
		window.location.assign(url);
	};
	#onScroll = () => {
		if (this.#rafId != null) return;
		this.#rafId = requestAnimationFrame(() => {
			this.#rafId = null;
			this.#updateProgress();
		});
	};
	#updateProgress() {
		if (!this.#scroller || !this.#bar) return;
		const { scrollLeft, scrollWidth, clientWidth } = this.#scroller;
		const maxScroll = scrollWidth - clientWidth;
		this.classList.toggle("is-scrollable", maxScroll > 4);
		const progress = scrollWidth > 0 ? Math.min(1, (scrollLeft + clientWidth) / scrollWidth) : 1;
		this.#bar.style.width = `${progress * 100}%`;
	}
	#isScrollable() {
		if (!this.#scroller) return false;
		const { scrollWidth, clientWidth } = this.#scroller;
		return scrollWidth - clientWidth > 4;
	}
	/** @param {PointerEvent} event */
	#onPointerDown = (event) => {
		if (!this.#scroller || event.pointerType === "touch") return;
		if (event.button !== 0) return;
		if (!(event.target instanceof Element)) return;
		if (event.target.closest("a")) return;
		if (!this.#isScrollable()) return;
		this.#dragging = true;
		this.#moved = false;
		this.#startX = event.clientX;
		this.#startScroll = this.#scroller.scrollLeft;
		this.#scroller.addEventListener("pointermove", this.#onPointerMove);
		this.#scroller.addEventListener("pointerup", this.#onPointerUp);
		this.#scroller.addEventListener("pointercancel", this.#onPointerUp);
	};
	/** @param {PointerEvent} event */
	#onPointerMove = (event) => {
		if (!this.#dragging || !this.#scroller) return;
		const delta = event.clientX - this.#startX;
		if (!this.#moved) {
			if (Math.abs(delta) < DRAG_THRESHOLD) return;
			this.#moved = true;
			this.#scroller.setPointerCapture(event.pointerId);
		}
		event.preventDefault();
		this.#scroller.scrollLeft = this.#startScroll - delta;
	};
	/** @param {PointerEvent} event */
	#onPointerUp = (event) => {
		if (!this.#scroller) return;
		const moved = this.#moved;
		this.#dragging = false;
		this.#unbindDrag();
		if (this.#scroller.hasPointerCapture?.(event.pointerId)) this.#scroller.releasePointerCapture(event.pointerId);
		if (moved) {
			const preventClick = (clickEvent) => {
				clickEvent.preventDefault();
				clickEvent.stopPropagation();
				this.#scroller?.removeEventListener("click", preventClick, true);
			};
			this.#scroller.addEventListener("click", preventClick, true);
		}
	};
	#unbindDrag() {
		this.#scroller?.removeEventListener("pointermove", this.#onPointerMove);
		this.#scroller?.removeEventListener("pointerup", this.#onPointerUp);
		this.#scroller?.removeEventListener("pointercancel", this.#onPointerUp);
	}
};
if (!customElements.get("vp-quick-categories")) customElements.define("vp-quick-categories", VpQuickCategories);
//#endregion
